import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useOnboarding } from './ImportJobContext';
import { useScrollLock } from '../../_utils/useScrollLock';
import ImportStaging from './ImportStaging';
import NeedsYouQueue from './NeedsYouQueue';
import RevealScreen from './RevealScreen';
import GoLiveScreen from './GoLiveScreen';
import { StartScreen, StarterMenuScreen } from './StarterScreen';
import toast from '../shared/Toast';
import { SETUP_V2_ENABLED } from './flags';
import { openSetup as openSetupV2 } from './setup/SetupContext';
import { IconClose } from './icons';
import './OnboardingLayer.css';
import { setupOff as receptionSetupOff } from '../../_utils/receptionMode';

/**
 * The onboarding / import layer (plan §4.1, §5).
 *
 * A dismissible layer over the app, never a locked screen:
 *   desktop = centred XL modal with close; mobile = full-height dark sheet.
 * Screens: import → ex (needs you) → reveal. mode="import" is the post-setup
 * "Bring more in": the same story screen, but the primary action commits and
 * closes rather than continuing to the setup questions.
 *
 * Rendered once at the router level by OnboardingHost; opened through the
 * context (useOnboarding().openLayer) or the 'beu:onboarding-open' event.
 */

const useIsMobile = () => {
  const [m, setM] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = (e) => setM(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return m;
};

export default function OnboardingLayer({ salonName }) {
  const ob = useOnboarding();
  const { layer, closeLayer, job, commit, patchOnboarding, onboarding, startNewJob } = ob;
  const isMobile = useIsMobile();
  const [screen, setScreen] = useState('import');
  const [committing, setCommitting] = useState(false);
  const rootRef = useRef(null);
  const open = !!layer.open;
  useScrollLock(open);

  // On phones the layer is full-screen but sits below the fixed app header
  // (kept under SlidePanel so merge panels stack). Hide the header instead.
  useEffect(() => {
    document.body.classList.toggle('ob-layer-open', open);
    return () => document.body.classList.remove('ob-layer-open');
  }, [open]);

  // Resume at the right screen when (re)opened.
  useEffect(() => {
    if (!open) return;
    // Set up with Bailey replaces the setup screens (the welcome fork, the
    // starter menu and go live). The "needs you" queue and "Bring more in"
    // stay here.
    // Never setup on a device locked in reception mode.
    if (layer.mode === 'setup' && receptionSetupOff()) { closeLayer(); return; }
    if (SETUP_V2_ENABLED && layer.mode === 'setup' && layer.screen !== 'ex') {
      closeLayer();
      const chapter = { live: 'ready', menu: 'menu', import: 'files' }[layer.screen];
      openSetupV2({ chapter });
      return;
    }
    // Each post-setup open is a fresh job so results never mix with a previous import.
    if (layer.mode === 'import') { startNewJob(); setScreen('import'); return; }
    if (layer.screen) { setScreen(layer.screen); return; }
    if (job && ['committed', 'committing'].includes(job.status)) {
      const pending = (job.queue || []).filter((q) => !(job.answers || {})[q.id]);
      setScreen(pending.length ? 'ex' : 'reveal');
    } else if (job && (job.files || []).length) {
      setScreen('import');
    } else {
      // First run with nothing uploaded: the welcome fork (plan Step A).
      setScreen('start');
    }
  }, [open, layer.mode, layer.screen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc closes (web). Focus the dialog on open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeLayer(); };
    window.addEventListener('keydown', onKey);
    setTimeout(() => rootRef.current && rootRef.current.focus(), 30);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeLayer]);

  const onSkipSetup = useCallback(() => {
    patchOnboarding({ status: onboarding?.status === 'completed' ? 'completed' : 'dismissed' });
    closeLayer();
  }, [patchOnboarding, closeLayer, onboarding]);

  // "Continue setup": commit the job (plan §2.4) and move on to the queue.
  const onContinue = useCallback(async () => {
    if (committing) return;
    setCommitting(true);
    try {
      if (job && !['committing', 'committed'].includes(job.status)) await commit();
      setScreen('ex');
    } catch (e) {
      toast.error(e.message || 'Could not start the import. Please try again.');
    } finally {
      setCommitting(false);
    }
  }, [commit, committing, job]);

  // Post-setup "Add to my salon": commit and close. Anything the import needs
  // answering lands in Clients → Import, as before.
  const onAddMore = useCallback(async () => {
    if (committing) return;
    setCommitting(true);
    try {
      if (job && !['committing', 'committed'].includes(job.status)) await commit();
      toast.success('Added. Anything that needs you is in Clients → Import.');
      closeLayer();
      window.dispatchEvent(new CustomEvent('diary:booking-created'));
    } catch (e) {
      toast.error(e.message || 'Could not add that. Please try again.');
    } finally {
      setCommitting(false);
    }
  }, [commit, committing, job, closeLayer]);

  const onQueueDone = useCallback(() => setScreen('reveal'), []);
  const onRevealDone = useCallback((how) => {
    // Lean flow: 'tour' closes onto the diary where the first-tasks card
    // takes over; 'live' (skip) goes straight to the go-live screen.
    if (how === 'live' && !onboarding?.checklist?.live) { setScreen('live'); return; }
    closeLayer();
    if (how === 'live') toast.success('Your booking page is live');
    window.dispatchEvent(new CustomEvent('diary:booking-created')); // refresh the diary behind
  }, [closeLayer, onboarding]);
  // Starter setup finished: close onto the diary, where the practice booking
  // and the first-tasks card are waiting.
  const onStarterDone = useCallback(({ created, practice, note }) => {
    closeLayer();
    window.dispatchEvent(new CustomEvent('diary:booking-created'));
    window.dispatchEvent(new CustomEvent('beu:catalogue-changed'));
    if (practice && practice.date) window.dispatchEvent(new CustomEvent('diary:go-to-date', { detail: { date: practice.date } }));
    if (note) toast.info(note);
    else toast.success(created ? `${created} ${created === 1 ? 'service' : 'services'} added. Your practice booking is in the diary.` : 'You are set up. Your practice booking is in the diary.');
  }, [closeLayer]);

  const onLiveDone = useCallback((how) => {
    closeLayer();
    window.dispatchEvent(new CustomEvent('diary:booking-created')); // practice booking may have been removed
    if (how === 'live') toast.success('Your booking page is live');
  }, [closeLayer]);

  if (!open) return null;

  const isImportMode = layer.mode === 'import';
  // The welcome fork is short, so it hugs its content rather than leaving a
  // half screen of white under two buttons.
  const frameClass = isImportMode || screen === 'import'
    ? 'ob-modal'
    : `ob-modal ${screen === 'start' ? 'ob-modal--compact ob-modal--start' : 'ob-modal--narrow'}`;
  // The close sits over the charcoal story on the import screen (light), and
  // over white everywhere else (dark).
  const lightClose = isImportMode || screen === 'import';

  return ReactDOM.createPortal(
    <div className="ob-root" role="dialog" aria-modal="true" aria-label={isImportMode ? 'Bring more in' : 'Set up your salon'} ref={rootRef} tabIndex={-1}>
      {/* Clicking off the modal closes it. In setup mode that is the same as
          "I'll set things up myself": the checklist card takes over. */}
      <div className="ob-backdrop" onClick={isImportMode ? closeLayer : onSkipSetup} />
      <div className={frameClass}>
        <button
          type="button"
          className={`ob-close ${lightClose ? '' : 'ob-close--dark'}`}
          onClick={isImportMode ? closeLayer : onSkipSetup}
          title={isImportMode ? 'Close' : 'Set up later'}
          aria-label={isImportMode ? 'Close' : 'Set up later'}
        >
          <IconClose />
        </button>
        {isImportMode && (
          <ImportStaging variant="more" salonName={salonName || 'your salon'} onContinue={onAddMore} onSkip={closeLayer} isMobile={isMobile} committing={committing} />
        )}
        {!isImportMode && screen === 'start' && (
          <StartScreen salonName={salonName} onStarter={() => setScreen('menu')} onImport={() => setScreen('import')} onSkip={onSkipSetup} />
        )}
        {!isImportMode && screen === 'menu' && (
          <StarterMenuScreen onBack={() => setScreen('start')} onDone={onStarterDone} />
        )}
        {!isImportMode && screen === 'import' && (
          <ImportStaging salonName={salonName || 'your salon'} onContinue={onContinue} onSkip={onSkipSetup} isMobile={isMobile} committing={committing} />
        )}
        {!isImportMode && screen === 'ex' && <NeedsYouQueue onDone={onQueueDone} isMobile={isMobile} />}
        {!isImportMode && screen === 'reveal' && <RevealScreen onDone={onRevealDone} />}
        {!isImportMode && screen === 'live' && <GoLiveScreen onDone={onLiveDone} onOpenPath={closeLayer} />}
      </div>
    </div>,
    document.body,
  );
}
